import { useCallback, useEffect, useState } from "react";
import { ShieldCheck, ShieldOff } from "lucide-react";
import { fmtINR } from "@/lib/service";

const KEY = "svc-ex-warranty";
const EVT = "svc-ex-warranty-change";

// One switch for every After-Sales page: GS figures include warranty revenue by default; "Ex-warranty" shows GST billing alone.
export function useExWarranty() {
  const [ex, setExState] = useState(() => localStorage.getItem(KEY) === "1");
  useEffect(() => {
    const sync = () => setExState(localStorage.getItem(KEY) === "1");
    window.addEventListener(EVT, sync);
    return () => window.removeEventListener(EVT, sync);
  }, []);
  const setEx = useCallback((v) => {
    localStorage.setItem(KEY, v ? "1" : "0");
    window.dispatchEvent(new Event(EVT));
  }, []);
  return [ex, setEx];
}

export function WarrantyToggle({ ex, onChange }) {
  return (
    <div className="flex items-center rounded-md border overflow-hidden text-[11px]" role="group" aria-label="Warranty basis" data-testid="svc-warranty-toggle">
      <button type="button" onClick={() => onChange(false)} className={`px-2.5 py-1.5 font-semibold inline-flex items-center gap-1 transition-colors ${!ex ? "bg-foreground text-background" : "text-muted-foreground hover:bg-secondary"}`} data-testid="svc-warranty-incl" title="GS figures include warranty labour & parts (default)"><ShieldCheck size={12} /> Incl. warranty</button>
      <button type="button" onClick={() => onChange(true)} className={`px-2.5 py-1.5 font-semibold inline-flex items-center gap-1 transition-colors ${ex ? "bg-foreground text-background" : "text-muted-foreground hover:bg-secondary"}`} data-testid="svc-warranty-ex" title="GST RO billing only — warranty excluded"><ShieldOff size={12} /> Ex-warranty</button>
    </div>
  );
}

export const basisText = (ex) => (ex ? "ex-warranty (GST billing only)" : "incl. warranty");

// Compact split line: totals + where the matched ROs sit. `w` = {labour, parts, total, claims, by_cat}
export function WarrantyStrip({ w, meta, perRo, ex, testId = "svc-warranty-strip" }) {
  if (ex) {
    return (
      <div className="rounded-md border border-dashed px-4 py-2 text-xs text-muted-foreground flex items-center gap-2" data-testid={testId}>
        <ShieldOff size={13} /> Ex-warranty view — GS Labour, Parts and Revenue show GST RO billing only. Switch to “Incl. warranty” for the reported figures.
      </div>
    );
  }
  if (!w) return null;
  const keys = meta?.split_keys || [];
  return (
    <div className="rounded-md border bg-card px-4 py-2.5 text-xs flex flex-wrap items-center gap-x-5 gap-y-1" data-testid={testId}>
      <span className="font-heading font-semibold flex items-center gap-1.5"><ShieldCheck size={14} /> Warranty revenue <b className="num" data-testid={`${testId}-total`}>₹{fmtINR(w.total)}</b></span>
      <span className="text-muted-foreground">labour <b className="num text-foreground">₹{fmtINR(w.labour)}</b></span>
      <span className="text-muted-foreground">parts <b className="num text-foreground">₹{fmtINR(w.parts)}</b></span>
      {perRo != null && <span className="text-muted-foreground">per GS RO <b className="num text-foreground">₹{fmtINR(perRo)}</b></span>}
      <span className="text-muted-foreground">{w.claims} claims</span>
      <span className="hidden md:inline text-muted-foreground">·</span>
      {keys.filter((k) => w.by_cat?.[k.key] > 0).map((k) => (
        <span key={k.key} className={`rounded px-1.5 py-0.5 ${k.key === "unmatched" ? "bg-amber-50 text-amber-900 border border-amber-200" : "bg-secondary"}`} data-testid={`${testId}-${k.key}`}>{k.label.replace(" ROs", "")} <b className="num">₹{fmtINR(w.by_cat[k.key])}</b></span>
      ))}
      <span className="text-[10px] text-muted-foreground w-full">Included in GS Labour / Parts / Revenue · dated by warranty invoice · per-RO still divides by GS ROs (warranty adds no ROs)</span>
    </div>
  );
}
